import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatPrice } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { ArrowUpRight, Award, Package, RefreshCw, Sparkles } from "lucide-react";

const OCCASION_CARDS = [
  { key: "bridal", label: "Bridal", img: "https://images.pexels.com/photos/13204678/pexels-photo-13204678.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900" },
  { key: "festive", label: "Festive", img: "https://images.pexels.com/photos/28405815/pexels-photo-28405815.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900" },
  { key: "cocktail", label: "Cocktail", img: "https://images.pexels.com/photos/17040892/pexels-photo-17040892.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900" },
  { key: "everyday", label: "Everyday", img: "https://images.unsplash.com/photo-1763400234383-8b9ecbb9c043?crop=entropy&cs=srgb&fm=jpg&w=900&h=1200&q=85" },
];

const CRAFT_CARDS = [
  { key: "dabka", label: "Dabka Work", note: "Metallic-thread flowerwork on silk", img: "https://images.unsplash.com/photo-1763400234383-8b9ecbb9c043?crop=entropy&cs=srgb&fm=jpg&w=900&h=1200&q=85" },
  { key: "zardozi", label: "Zardozi", note: "Antique gold and silver couching", img: "https://images.pexels.com/photos/13204678/pexels-photo-13204678.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900" },
];

const INSTA = [
  "https://images.pexels.com/photos/17040892/pexels-photo-17040892.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=600",
  "https://images.pexels.com/photos/13204678/pexels-photo-13204678.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=600",
  "https://images.pexels.com/photos/28405815/pexels-photo-28405815.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=600",
  "https://images.unsplash.com/photo-1763400234383-8b9ecbb9c043?crop=entropy&cs=srgb&fm=jpg&w=600&h=600&q=85",
  "https://images.pexels.com/photos/17040892/pexels-photo-17040892.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=600",
  "https://images.pexels.com/photos/13204678/pexels-photo-13204678.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=600",
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const { currency } = useCurrency();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products?featured=true&limit=8");
        setFeatured(data);
      } catch {}
    })();
  }, []);

  return (
    <main className="pt-16 md:pt-28" data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="grid md:grid-cols-12 min-h-[80vh]">
          <div className="md:col-span-5 order-2 md:order-1 flex flex-col justify-center px-6 md:px-14 py-16">
            <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B] mb-6">The Bridal Edit · Autumn ’26</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-[#1A1A1A] tracking-tight">
              Woven in gold,<br /> worn as heirlooms.
            </h1>
            <p className="mt-6 text-[15px] leading-relaxed text-[#4A4A4A] max-w-md">
              A hand-embroidered anthology of anarkalis, lehengas and drape sarees — crafted by artisans
              in the old bylanes of Delhi, made to be passed down.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/collections/bridal" className="btn-editorial" data-testid="hero-cta-bridal">
                Shop Bridal
              </Link>
              <Link to="/about" className="text-[11px] uppercase tracking-nav underline underline-offset-8 decoration-[#BCA37F] text-[#1A1A1A] hover:text-[#5C1A1B] transition-colors" data-testid="hero-cta-story">
                Read the Atelier Story
              </Link>
            </div>
          </div>
          <div className="md:col-span-7 order-1 md:order-2 relative">
            <img
              src="https://images.pexels.com/photos/13204678/pexels-photo-13204678.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900"
              alt="Model in Dabkazari bridal anarkali"
              className="w-full h-[70vh] md:h-full object-cover"
              data-testid="hero-image"
            />
            <div className="hidden md:block absolute bottom-8 right-8 bg-[#FDFBF7]/95 backdrop-blur px-6 py-4 border-l-2 border-[#5C1A1B] max-w-[260px]">
              <p className="font-serif text-lg leading-tight">Rani Anarkali</p>
              <p className="text-[11px] uppercase tracking-nav text-[#7A7A7A] mt-1">Featured · Bridal</p>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-[#BCA37F]/25 bg-[#F3EFEA]/50" data-testid="trust-bar">
        <div className="px-6 md:px-12 lg:px-16 py-5 flex flex-wrap items-center justify-center gap-x-14 gap-y-3 text-[11px] uppercase tracking-editorial text-[#4A4A4A]">
          <span className="flex items-center gap-2"><Package size={14} strokeWidth={1.3} /> Worldwide Shipping</span>
          <span className="flex items-center gap-2"><Award size={14} strokeWidth={1.3} /> Authentic Handcraft</span>
          <span className="flex items-center gap-2"><RefreshCw size={14} strokeWidth={1.3} /> Easy 7-day Returns</span>
          <span className="flex items-center gap-2"><Sparkles size={14} strokeWidth={1.3} /> Bespoke Sizing</span>
        </div>
      </section>

      {/* SHOP BY OCCASION */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32" data-testid="occasion-section">
        <div className="flex items-end justify-between mb-14">
          <div>
            <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B] mb-3">Shop by Occasion</p>
            <h2 className="font-serif text-4xl md:text-5xl">Ateliers for every ceremony.</h2>
          </div>
          <Link to="/collections/bridal" className="hidden md:inline text-[11px] uppercase tracking-nav hover:text-[#5C1A1B] transition-colors">View all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {OCCASION_CARDS.map((o, idx) => (
            <motion.div
              key={o.key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: idx * 0.06 }}
            >
              <Link to={`/collections/${o.key}`} className="group block" data-testid={`occ-card-${o.key}`}>
                <div className="aspect-[3/4] overflow-hidden bg-[#F3EFEA] relative">
                  <img src={o.img} alt={o.label} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[900ms] ease-out" />
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <p className="font-serif text-2xl">{o.label}</p>
                  <ArrowUpRight size={16} strokeWidth={1.3} className="text-[#5C1A1B] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 bg-[#F3EFEA]/40" data-testid="featured-section">
          <div className="flex items-end justify-between mb-14">
            <div>
              <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B] mb-3">The Editor’s Edit</p>
              <h2 className="font-serif text-4xl md:text-5xl">Pieces we’re loving right now.</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 md:gap-x-6 gap-y-12">
            {featured.map((p) => (
              <Link to={`/product/${p.slug}`} key={p.product_id} className="product-card group" data-testid={`featured-${p.slug}`}>
                <div className="relative aspect-[3/4] overflow-hidden bg-[#F3EFEA]">
                  <img src={p.images[0]} alt={p.name} className="img-primary w-full h-full object-cover" loading="lazy" />
                  {p.images[1] && (
                    <img src={p.images[1]} alt={`${p.name} alt`} className="img-secondary w-full h-full object-cover" loading="lazy" />
                  )}
                  {p.compare_at_price_inr && (
                    <span className="absolute top-3 left-3 bg-[#FDFBF7] text-[#5C1A1B] text-[10px] uppercase tracking-nav px-2 py-1">
                      Save {Math.round(100 - (p.price_inr / p.compare_at_price_inr) * 100)}%
                    </span>
                  )}
                </div>
                <div className="mt-4">
                  <p className="font-serif text-xl leading-tight">{p.name}</p>
                  <p className="text-[11px] uppercase tracking-nav text-[#7A7A7A] mt-1">{p.category}</p>
                  <p className="text-sm text-[#1A1A1A] mt-2">{formatPrice(p.price_inr, currency)}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* SHOP BY CRAFT */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32" data-testid="craft-section">
        <div className="mb-14">
          <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B] mb-3">Shop by Craft</p>
          <h2 className="font-serif text-4xl md:text-5xl max-w-2xl">Hands that have kept centuries alive.</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-8 md:gap-14">
          {CRAFT_CARDS.map((c) => (
            <Link key={c.key} to={`/craft/${c.key}`} className="group block" data-testid={`craft-card-${c.key}`}>
              <div className="aspect-[16/10] overflow-hidden bg-[#F3EFEA]">
                <img src={c.img} alt={c.label} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[900ms] ease-out" />
              </div>
              <p className="font-serif text-3xl mt-6">{c.label}</p>
              <p className="text-sm text-[#4A4A4A] mt-1 max-w-md">{c.note}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* OUR STORY */}
      <section className="relative bg-[#5C1A1B] text-[#FDFBF7]" data-testid="story-section">
        <div className="grid md:grid-cols-12">
          <div className="md:col-span-7 md:min-h-[80vh]">
            <img src="https://images.pexels.com/photos/28405815/pexels-photo-28405815.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900" alt="Atelier" loading="lazy" className="w-full h-full object-cover object-center" />
          </div>
          <div className="md:col-span-5 flex flex-col justify-center px-6 md:px-14 py-20">
            <p className="text-[11px] uppercase tracking-nav text-[#BCA37F] mb-6">Our Story</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">A quiet couture house, keeping dabka alive.</h2>
            <p className="mt-6 text-[15px] leading-relaxed text-[#FDFBF7]/85 max-w-md">
              Dabkazari began in a two-room karkhana in Shahjahanabad, where three karigars sat over
              embroidery frames. Two decades later, we still make every piece the slow way — one
              stitch, one story at a time.
            </p>
            <Link to="/about" className="mt-10 inline-block text-[11px] uppercase tracking-nav underline underline-offset-8 decoration-[#BCA37F] hover:text-[#BCA37F] transition-colors" data-testid="story-cta">
              Read our story
            </Link>
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="px-6 md:px-12 lg:px-16 py-24" data-testid="instagram-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B] mb-3">The Atelier · @dabkazari</p>
            <h2 className="font-serif text-3xl md:text-4xl">Worn by our world.</h2>
          </div>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3">
          {INSTA.map((src, i) => (
            <a key={i} href="#" className="aspect-square overflow-hidden bg-[#F3EFEA] group" data-testid={`insta-${i}`}>
              <img src={src} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700" />
            </a>
          ))}
        </div>
      </section>
    </main>
  );
}
